import { MCP_PUBLIC_URL, SINGLETON_ENDPOINT_ID, TENANT_NAME } from './governedMcpConstants';
import type {
  AccessPolicy,
  ActivityEvent,
  ExternalMcpConnection,
  DataModelSource,
  DirectoryGroup,
  DirectoryUser,
  GatewayApiKey,
  QuotaDailyPoint,
  QuotaModelSpend,
  QuotaUser,
  McpClient,
  McpEndpoint,
  PublishedCapability,
  SecuritySwitches,
  ToolGroup,
  TraceStep,
  WorkflowSource,
} from './governedMcpTypes';

export const DIRECTORY_USERS: DirectoryUser[] = [
  { id: 'u-priya', name: 'Priya Shah', email: 'priya.shah@acme.financial', title: 'Chief Risk Officer' },
  { id: 'u-marcus', name: 'Marcus Chen', email: 'marcus.chen@acme.financial', title: 'Claims Operations Lead' },
  { id: 'u-elena', name: 'Elena Rossi', email: 'elena.rossi@acme.financial', title: 'Legal Counsel' },
  { id: 'u-james', name: 'James Okonkwo', email: 'james.okonkwo@acme.financial', title: 'KYC Analyst' },
  { id: 'u-service', name: 'Claims Batch Service', email: 'svc-claims@acme.financial', title: 'Workload identity' },
];

export const DIRECTORY_GROUPS: DirectoryGroup[] = [
  { id: 'g-risk', name: 'Risk Analytics', description: 'Enterprise risk and exposure analysts', memberCount: 18 },
  { id: 'g-claims', name: 'Claims Operations', description: 'Adjusters and claims supervisors', memberCount: 42 },
  { id: 'g-legal', name: 'Legal & Compliance', description: 'Counsel and policy reviewers', memberCount: 11 },
  { id: 'g-kyc', name: 'KYC Operations', description: 'Customer due-diligence analysts', memberCount: 9 },
];

export const DATA_MODELS: DataModelSource[] = [
  {
    id: 'dm-exposure',
    name: 'Customer Exposure',
    code: 'CUST_EXPOSURE',
    description: 'Aggregated credit, insurance, and market exposure by customer and book.',
    classification: 'confidential',
    fields: ['customer_id', 'legal_name', 'book', 'exposure_usd', 'limit_usd', 'rating', 'as_of_date'],
    connectorKey: 'postgresql',
    backing: 'postgres',
    tables: [
      {
        id: 'risk.customer_exposure',
        name: 'customer_exposure',
        kind: 'table',
        fields: [
          { name: 'customer_id', dataType: 'uuid' },
          { name: 'legal_name', dataType: 'text' },
          { name: 'book', dataType: 'text' },
          { name: 'exposure_usd', dataType: 'numeric' },
          { name: 'limit_usd', dataType: 'numeric' },
          { name: 'rating', dataType: 'text' },
          { name: 'as_of_date', dataType: 'date' },
        ],
      },
      {
        id: 'risk.exposure_history',
        name: 'exposure_history',
        kind: 'table',
        fields: [
          { name: 'customer_id', dataType: 'uuid' },
          { name: 'as_of_date', dataType: 'date' },
          { name: 'exposure_usd', dataType: 'numeric' },
        ],
      },
    ],
  },
  {
    id: 'dm-policy',
    name: 'Policy Terms',
    code: 'POLICY_TERMS',
    description: 'In-force policy language, riders, and exclusion clauses.',
    classification: 'internal',
    fields: ['policy_id', 'product', 'clause_id', 'clause_text', 'effective_from', 'jurisdiction'],
    connectorKey: 'mssql',
    backing: 'mssql',
    tables: [
      {
        id: 'dbo.PolicyTerms',
        name: 'PolicyTerms',
        kind: 'table',
        fields: [
          { name: 'policy_id', dataType: 'nvarchar' },
          { name: 'product', dataType: 'nvarchar' },
          { name: 'clause_id', dataType: 'nvarchar' },
          { name: 'clause_text', dataType: 'nvarchar' },
          { name: 'effective_from', dataType: 'datetime' },
          { name: 'jurisdiction', dataType: 'nvarchar' },
        ],
      },
    ],
  },
  {
    id: 'dm-claims',
    name: 'Claims History',
    code: 'CLAIMS_HISTORY',
    description: 'Closed and open claims with loss amounts, status, and adjuster notes.',
    classification: 'restricted',
    fields: ['claim_id', 'customer_id', 'status', 'loss_usd', 'opened_at', 'ssn_last4', 'notes'],
    connectorKey: 'mssql',
    backing: 'mssql',
    tables: [
      {
        id: 'dbo.Claims',
        name: 'Claims',
        kind: 'table',
        fields: [
          { name: 'claim_id', dataType: 'nvarchar' },
          { name: 'customer_id', dataType: 'nvarchar' },
          { name: 'status', dataType: 'nvarchar' },
          { name: 'loss_usd', dataType: 'decimal' },
          { name: 'opened_at', dataType: 'datetime' },
          { name: 'ssn_last4', dataType: 'nvarchar' },
          { name: 'notes', dataType: 'nvarchar' },
        ],
      },
    ],
  },
  {
    id: 'dm-drive-kyc',
    name: 'KYC Evidence Drive',
    code: 'KYC_DRIVE',
    description: 'Customer due-diligence files stored in Google Drive.',
    classification: 'restricted',
    fields: ['file_id', 'name', 'mime_type', 'folder_id', 'modified_at'],
    connectorKey: 'google_drive',
    backing: 'google_drive',
    tables: [
      {
        id: 'drive/kyc/passports',
        name: 'KYC passports',
        kind: 'file',
        fields: [
          { name: 'file_id', dataType: 'string' },
          { name: 'name', dataType: 'string' },
          { name: 'mime_type', dataType: 'string' },
          { name: 'folder_id', dataType: 'string' },
          { name: 'modified_at', dataType: 'datetime' },
        ],
      },
      {
        id: 'drive/kyc/statements',
        name: 'Bank statements',
        kind: 'file',
        fields: [
          { name: 'file_id', dataType: 'string' },
          { name: 'name', dataType: 'string' },
          { name: 'mime_type', dataType: 'string' },
          { name: 'folder_id', dataType: 'string' },
        ],
      },
    ],
  },
];

export const WORKFLOWS: WorkflowSource[] = [
  {
    id: 'wf-adjust',
    name: 'Submit Claims Adjustment',
    code: 'WF_CLAIMS_ADJUST',
    description: 'Posts a claims reserve or payment adjustment through the governed claims workflow.',
    longRunning: false,
    destructive: true,
    hasSideEffects: true,
    isIdempotent: false,
    requiresApproval: true,
  },
  {
    id: 'wf-freeze',
    name: 'Freeze Account',
    code: 'WF_ACCT_FREEZE',
    description: 'Places a customer account in freeze pending fraud or sanctions review.',
    longRunning: false,
    destructive: true,
    hasSideEffects: true,
    isIdempotent: true,
    requiresApproval: true,
  },
  {
    id: 'wf-kyc',
    name: 'KYC Refresh',
    code: 'WF_KYC_REFRESH',
    description: 'Long-running customer due-diligence refresh across screening vendors.',
    longRunning: true,
    destructive: false,
    hasSideEffects: true,
    isIdempotent: true,
    requiresApproval: false,
  },
  {
    id: 'wf-recalc',
    name: 'Quarterly Risk Recalc',
    code: 'WF_Q_RISK_RECALC',
    description: 'Recomputes book-level risk factors; typically runs hours to complete.',
    longRunning: true,
    destructive: false,
    hasSideEffects: true,
    isIdempotent: true,
    requiresApproval: true,
  },
];

export const EXTERNAL_MCP_CONNECTIONS: ExternalMcpConnection[] = [
  {
    id: 'mcp-playwright',
    name: 'Playwright Browser',
    code: 'MCP-PLAYWRIGHT',
    description: 'External Playwright MCP server for governed browser automation (sandbox only).',
    publisher: 'Microsoft / Selfic catalog',
    tools: [
      {
        name: 'browser_navigate',
        description: 'Navigate the sandboxed browser to an approved URL.',
        inputSchema: '{ "url": "string" }',
      },
      {
        name: 'browser_snapshot',
        description: 'Capture accessibility snapshot of the current page.',
        inputSchema: '{ "fullPage": "boolean" }',
      },
    ],
    resources: [
      {
        uri: 'playwright://session/current',
        name: 'Current browser session',
        description: 'Active sandbox browser session metadata.',
      },
    ],
  },
  {
    id: 'mcp-github',
    name: 'GitHub Enterprise',
    code: 'MCP-GITHUB',
    description: 'External GitHub MCP connection scoped to the Acme engineering org.',
    publisher: 'GitHub / Selfic catalog',
    tools: [
      {
        name: 'search_issues',
        description: 'Search issues in allowed repositories.',
        inputSchema: '{ "query": "string", "repo": "string" }',
      },
      {
        name: 'get_pull_request',
        description: 'Read pull request metadata and files.',
        inputSchema: '{ "owner": "string", "repo": "string", "number": "number" }',
      },
    ],
    resources: [
      {
        uri: 'github://acme-financial/risk-engine',
        name: 'risk-engine repository',
        description: 'Governed repository resource.',
      },
    ],
  },
];

const DEFAULT_SECURITY: SecuritySwitches = {
  requireAuthenticatedUsers: true,
  requireRegisteredClients: true,
  propagateEnterpriseIdentity: true,
  enforceSelficPermissions: true,
  enableOutputGovernance: true,
  enableSensitiveDataMasking: true,
  enableCompleteAuditLogging: true,
  applyRateLimits: true,
  rateLimitPerMinute: 120,
};

export const SEED_ENDPOINTS: McpEndpoint[] = [
  {
    id: SINGLETON_ENDPOINT_ID,
    name: 'Selfic MCP',
    description: 'Governed MCP host for this tenant.',
    environment: 'production',
    slug: 'mcp',
    url: MCP_PUBLIC_URL,
    transport: 'streamable-http',
    compatibilitySse: true,
    authMethod: 'universal_token',
    identityProvider: 'okta',
    oidc: {
      issuerUrl: 'https://acme.okta.com/oauth2/default',
      clientId: '0oa-finance-mcp',
      scopes: 'openid profile mcp.invoke',
      audience: 'selfic-mcp',
    },
    enabled: true,
    security: { ...DEFAULT_SECURITY, rateLimitPerMinute: 90 },
    credentialVersion: 4,
    lastRotatedAt: '2026-08-12T09:14:00.000Z',
    requestVolume: 18420,
    lastActivityAt: '2026-09-16T18:42:11.000Z',
    createdAt: '2026-03-02T11:00:00.000Z',
    updatedAt: '2026-09-12T08:30:00.000Z',
    createdBy: 'Priya Shah',
  },
];

export const SEED_CLIENTS: McpClient[] = [
  {
    id: 'cl-claude',
    name: 'Claude Enterprise — Risk Desk',
    clientType: 'claude_enterprise',
    registrationId: 'mcp-client-claude-risk-01',
    authConfig: 'OIDC confidential client via Okta; user tokens only',
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'production',
    trustPolicy: 'named_users_groups',
    allowedUserIds: ['u-priya'],
    allowedGroupIds: ['g-risk'],
    status: 'active',
    createdBy: 'Priya Shah',
    lastConnectedAt: '2026-09-16T18:42:11.000Z',
    createdAt: '2026-03-04T10:00:00.000Z',
    updatedAt: '2026-09-12T08:30:00.000Z',
  },
  {
    id: 'cl-chatgpt',
    name: 'ChatGPT Enterprise — Legal',
    clientType: 'chatgpt_enterprise',
    registrationId: 'mcp-client-chatgpt-legal-01',
    authConfig: 'OIDC via Okta; never accept client-supplied user ids',
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'production',
    trustPolicy: 'named_users_groups',
    allowedUserIds: ['u-elena'],
    allowedGroupIds: ['g-legal'],
    status: 'active',
    createdBy: 'Elena Rossi',
    lastConnectedAt: '2026-09-16T15:21:00.000Z',
    createdAt: '2026-04-11T09:30:00.000Z',
    updatedAt: '2026-08-22T11:00:00.000Z',
  },
  {
    id: 'cl-copilot',
    name: 'Microsoft Copilot — HR Pilot',
    clientType: 'microsoft_copilot',
    registrationId: 'mcp-client-m365-hr-01',
    authConfig: 'Entra ID OIDC; Copilot Studio connector',
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'test',
    trustPolicy: 'enterprise_managed',
    allowedUserIds: [],
    allowedGroupIds: [],
    status: 'active',
    createdBy: 'Elena Rossi',
    lastConnectedAt: '2026-09-15T13:10:00.000Z',
    createdAt: '2026-06-20T08:00:00.000Z',
    updatedAt: '2026-09-01T10:12:00.000Z',
  },
  {
    id: 'cl-claims',
    name: 'Internal Claims Agent',
    clientType: 'internal_agent',
    registrationId: 'mcp-client-claims-agent-01',
    authConfig: 'Service identity + user delegation; Selfic maps IdP subject',
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'production',
    trustPolicy: 'service_identity',
    allowedUserIds: ['u-service'],
    allowedGroupIds: ['g-claims'],
    status: 'active',
    createdBy: 'Marcus Chen',
    lastConnectedAt: '2026-09-16T16:55:00.000Z',
    createdAt: '2026-03-18T14:00:00.000Z',
    updatedAt: '2026-09-08T09:40:00.000Z',
  },
];

export const SEED_TOOL_GROUPS: ToolGroup[] = [
  {
    id: 'grp-risk',
    name: 'Risk & Exposure',
    description: 'Read-only customer exposure and book-level risk tools.',
    createdAt: '2026-03-08T10:00:00.000Z',
    updatedAt: '2026-09-02T12:00:00.000Z',
  },
  {
    id: 'grp-policy',
    name: 'Policy Intelligence',
    description: 'Governed search over in-force policy language.',
    createdAt: '2026-04-12T11:00:00.000Z',
    updatedAt: '2026-08-19T09:00:00.000Z',
  },
  {
    id: 'grp-claims',
    name: 'Claims Operations',
    description: 'Side-effecting claims adjustments and related actions.',
    createdAt: '2026-03-20T15:00:00.000Z',
    updatedAt: '2026-09-08T09:40:00.000Z',
  },
  {
    id: 'grp-kyc',
    name: 'KYC & Diligence',
    description: 'Long-running customer due-diligence processes.',
    createdAt: '2026-05-22T10:00:00.000Z',
    updatedAt: '2026-09-05T16:20:00.000Z',
  },
  {
    id: 'grp-sandbox',
    name: 'Sandbox External',
    description: 'Re-published external MCP tools, isolated to development.',
    createdAt: '2026-08-01T09:00:00.000Z',
    updatedAt: '2026-09-10T14:22:00.000Z',
  },
];

export const SEED_PUBLISHED: PublishedCapability[] = [
  {
    id: 'pub-exposure',
    capabilityKind: 'information',
    backedBy: 'data_model',
    sourceId: 'dm-exposure',
    sourceName: 'Customer Exposure',
    mcpRepresentations: ['resource', 'tool'],
    mcpToolName: 'get_customer_exposure',
    mcpResourceUri: 'selfic://models/CUST_EXPOSURE/records',
    description: 'Retrieve governed customer exposure records for an authenticated analyst.',
    inputSchema: '{ "customer_id": "string", "as_of_date?": "date" }',
    outputSchema: '{ "customer_id": "string", "exposure_usd": "number", "limit_usd": "number", "rating": "string" }',
    useWhen: 'The user needs current or historical exposure for a known customer.',
    doNotUseWhen: 'Do not use to dump entire books or to infer unapproved customers.',
    usageGuidance: 'Prefer resource read for a known URI; use the tool when the agent has a customer_id.',
    endpointId: SINGLETON_ENDPOINT_ID,
    allowedClientIds: ['cl-claude', 'cl-chatgpt'],
    allowedUserIds: ['u-priya'],
    allowedGroupIds: ['g-risk'],
    environment: 'production',
    riskLevel: 'medium',
    dataClassification: 'confidential',
    rateLimitPerMinute: 30,
    maxRecords: 50,
    requiresApproval: false,
    outputGovernance: true,
    maskingEnabled: true,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '1.4.0',
    status: 'published',
    readOnly: true,
    destructive: false,
    hasSideEffects: false,
    isIdempotent: true,
    requiresConfirmation: false,
    selectedExistingToolNames: [],
    selectedExistingResourceUris: [],
    groupIds: ['grp-risk'],
    resourceSchema:
      '{ "type": "object", "properties": { "customer_id": { "type": "string" }, "as_of_date": { "type": "string", "format": "date" }, "exposure_usd": { "type": "number" }, "limit_usd": { "type": "number" }, "rating": { "type": "string" } }, "required": ["customer_id"] }',
    mcpCapability: 'tool',
    sourceKind: 'data_model',
    dataModelOperations: ['query', 'read'],
    selectedTableIds: ['risk.customer_exposure'],
    customResourceText: '',
    createdAt: '2026-03-08T10:00:00.000Z',
    updatedAt: '2026-09-02T12:00:00.000Z',
  },
  {
    id: 'pub-search-policy',
    capabilityKind: 'search',
    backedBy: 'data_model',
    sourceId: 'dm-policy',
    sourceName: 'Policy Terms',
    mcpRepresentations: ['tool'],
    mcpToolName: 'search_policy_terms',
    description: 'Semantic search over in-force policy clauses with governed result limits.',
    inputSchema: '{ "query": "string", "jurisdiction?": "string", "limit?": "number" }',
    outputSchema: '{ "hits": [{ "policy_id": "string", "clause_id": "string", "snippet": "string" }] }',
    useWhen: 'The user is looking up policy language, exclusions, or riders.',
    doNotUseWhen: 'Do not use to rewrite or bind coverage; this is retrieval only.',
    usageGuidance: 'Always pass a focused query. Respect max records.',
    endpointId: SINGLETON_ENDPOINT_ID,
    allowedClientIds: ['cl-chatgpt', 'cl-claude'],
    allowedUserIds: ['u-elena'],
    allowedGroupIds: ['g-legal'],
    environment: 'production',
    riskLevel: 'low',
    dataClassification: 'internal',
    rateLimitPerMinute: 60,
    maxRecords: 25,
    requiresApproval: false,
    outputGovernance: true,
    maskingEnabled: false,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '2.1.0',
    status: 'published',
    readOnly: true,
    destructive: false,
    hasSideEffects: false,
    isIdempotent: true,
    requiresConfirmation: false,
    selectedExistingToolNames: [],
    selectedExistingResourceUris: [],
    groupIds: ['grp-policy'],
    resourceSchema: '',
    mcpCapability: 'tool',
    sourceKind: 'data_model',
    dataModelOperations: ['query'],
    selectedTableIds: ['dbo.PolicyTerms'],
    customResourceText: '',
    createdAt: '2026-04-12T11:00:00.000Z',
    updatedAt: '2026-08-19T09:00:00.000Z',
  },
  {
    id: 'pub-adjust',
    capabilityKind: 'action',
    backedBy: 'workflow',
    sourceId: 'wf-adjust',
    sourceName: 'Submit Claims Adjustment',
    mcpRepresentations: ['tool'],
    mcpToolName: 'submit_claims_adjustment',
    description: 'Submit a claims reserve or payment adjustment. Destructive. Requires human approval.',
    inputSchema: '{ "claim_id": "string", "adjustment_usd": "number", "reason": "string" }',
    outputSchema: '{ "workflow_id": "string", "status": "pending_approval" }',
    useWhen: 'An authorized claims operator needs to post an adjustment on an existing claim.',
    doNotUseWhen: 'Do not invent claim ids, round amounts, or skip the stated reason.',
    usageGuidance: 'Always confirm amount and reason with the user before invoking.',
    endpointId: SINGLETON_ENDPOINT_ID,
    allowedClientIds: ['cl-claims'],
    allowedUserIds: ['u-marcus'],
    allowedGroupIds: ['g-claims'],
    environment: 'production',
    riskLevel: 'critical',
    dataClassification: 'restricted',
    rateLimitPerMinute: 8,
    maxRecords: 1,
    requiresApproval: true,
    outputGovernance: true,
    maskingEnabled: true,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '1.0.2',
    status: 'published',
    readOnly: false,
    destructive: true,
    hasSideEffects: true,
    isIdempotent: false,
    requiresConfirmation: true,
    selectedExistingToolNames: [],
    selectedExistingResourceUris: [],
    groupIds: ['grp-claims'],
    resourceSchema: '',
    mcpCapability: 'tool',
    sourceKind: 'workflow',
    dataModelOperations: [],
    selectedTableIds: [],
    customResourceText: '',
    createdAt: '2026-03-20T15:00:00.000Z',
    updatedAt: '2026-09-08T09:40:00.000Z',
  },
  {
    id: 'pub-kyc',
    capabilityKind: 'process',
    backedBy: 'long_running_workflow',
    sourceId: 'wf-kyc',
    sourceName: 'KYC Refresh',
    mcpRepresentations: ['tool', 'task'],
    mcpToolName: 'start_kyc_refresh',
    mcpTaskName: 'kyc_refresh_task',
    description: 'Start a long-running KYC refresh and poll task status.',
    inputSchema: '{ "customer_id": "string", "force?": "boolean" }',
    outputSchema: '{ "task_id": "string", "status": "queued|running|succeeded|failed" }',
    useWhen: 'Due diligence must be refreshed for a customer and the operator accepts async completion.',
    doNotUseWhen: 'Do not start a refresh solely to probe whether a customer exists.',
    usageGuidance: 'Invoke the start tool, then poll the task. Do not block the conversation on completion.',
    endpointId: SINGLETON_ENDPOINT_ID,
    allowedClientIds: ['cl-claims', 'cl-claude'],
    allowedUserIds: ['u-james'],
    allowedGroupIds: ['g-kyc'],
    environment: 'production',
    riskLevel: 'high',
    dataClassification: 'restricted',
    rateLimitPerMinute: 6,
    maxRecords: 1,
    requiresApproval: false,
    outputGovernance: true,
    maskingEnabled: true,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '3.0.0',
    status: 'published',
    readOnly: false,
    destructive: false,
    hasSideEffects: true,
    isIdempotent: true,
    requiresConfirmation: true,
    selectedExistingToolNames: [],
    selectedExistingResourceUris: [],
    groupIds: ['grp-kyc'],
    resourceSchema: '',
    mcpCapability: 'tool',
    sourceKind: 'workflow',
    dataModelOperations: [],
    selectedTableIds: [],
    customResourceText: '',
    createdAt: '2026-05-22T10:00:00.000Z',
    updatedAt: '2026-09-05T16:20:00.000Z',
  },
  {
    id: 'pub-browser',
    capabilityKind: 'external_mcp',
    backedBy: 'external_mcp',
    sourceId: 'mcp-playwright',
    sourceName: 'Playwright Browser',
    mcpRepresentations: ['tool', 'resource'],
    mcpToolName: 'browser_navigate',
    mcpResourceUri: 'playwright://session/current',
    description: 'Governed re-publish of the external Playwright navigate tool into the sandbox gateway.',
    inputSchema: '{ "url": "string" }',
    outputSchema: '{ "ok": "boolean", "title": "string" }',
    useWhen: 'The agent must open an allow-listed URL in the sandbox browser.',
    doNotUseWhen: 'Do not navigate to customer banking, PII, or unapproved domains.',
    usageGuidance: 'Only approved sandbox hosts. Selfic still authorizes every call.',
    endpointId: SINGLETON_ENDPOINT_ID,
    allowedClientIds: [],
    allowedUserIds: [],
    allowedGroupIds: [],
    environment: 'development',
    riskLevel: 'medium',
    dataClassification: 'internal',
    rateLimitPerMinute: 20,
    maxRecords: 1,
    requiresApproval: false,
    outputGovernance: true,
    maskingEnabled: true,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '0.9.0',
    status: 'published',
    readOnly: false,
    destructive: false,
    hasSideEffects: true,
    isIdempotent: false,
    requiresConfirmation: true,
    selectedExistingToolNames: ['browser_navigate'],
    selectedExistingResourceUris: ['playwright://session/current'],
    groupIds: ['grp-sandbox'],
    resourceSchema:
      '{ "type": "object", "properties": { "session_id": { "type": "string" }, "url": { "type": "string" }, "title": { "type": "string" }, "status": { "type": "string" } }, "required": ["session_id"] }',
    mcpCapability: 'tool',
    sourceKind: 'external_mcp',
    dataModelOperations: [],
    selectedTableIds: [],
    customResourceText: '',
    createdAt: '2026-08-01T09:00:00.000Z',
    updatedAt: '2026-09-10T14:22:00.000Z',
  },
];

export const SEED_POLICIES: AccessPolicy[] = [
  {
    id: 'pol-risk-read',
    name: 'Risk desk — exposure read',
    description: 'Allow Risk Analytics to discover and read customer exposure via Claude.',
    userIds: ['u-priya'],
    groupIds: ['g-risk'],
    clientIds: ['cl-claude'],
    endpointIds: [SINGLETON_ENDPOINT_ID],
    publishedCapabilityIds: ['pub-exposure'],
    allowDiscovery: true,
    allowInvocation: true,
    readOnly: true,
    environments: ['production'],
    timeWindow: 'Business hours 07:00–19:00 ET',
    rateLimitPerMinute: 30,
    maxRecords: 50,
    riskLevel: 'medium',
    requiresApproval: false,
    sensitiveDataRestricted: true,
    outputRestricted: true,
    inheritSelficPermissions: true,
    effect: 'allow',
    status: 'active',
    createdAt: '2026-03-08T12:00:00.000Z',
    updatedAt: '2026-09-02T12:00:00.000Z',
    createdBy: 'Priya Shah',
  },
  {
    id: 'pol-legal-search',
    name: 'Legal — policy term search',
    description: 'ChatGPT Enterprise may search policy terms for Legal & Compliance.',
    userIds: ['u-elena'],
    groupIds: ['g-legal'],
    clientIds: ['cl-chatgpt'],
    endpointIds: [SINGLETON_ENDPOINT_ID],
    publishedCapabilityIds: ['pub-search-policy'],
    allowDiscovery: true,
    allowInvocation: true,
    readOnly: true,
    environments: ['production', 'test'],
    timeWindow: 'Always',
    rateLimitPerMinute: 60,
    maxRecords: 25,
    riskLevel: 'low',
    requiresApproval: false,
    sensitiveDataRestricted: false,
    outputRestricted: false,
    inheritSelficPermissions: true,
    effect: 'allow',
    status: 'active',
    createdAt: '2026-04-12T12:00:00.000Z',
    updatedAt: '2026-08-19T09:00:00.000Z',
    createdBy: 'Elena Rossi',
  },
  {
    id: 'pol-claims-adjust',
    name: 'Claims adjustment — approval required',
    description: 'Claims agent may invoke adjustment workflow only with human approval.',
    userIds: ['u-marcus'],
    groupIds: ['g-claims'],
    clientIds: ['cl-claims'],
    endpointIds: [SINGLETON_ENDPOINT_ID],
    publishedCapabilityIds: ['pub-adjust'],
    allowDiscovery: true,
    allowInvocation: true,
    readOnly: false,
    environments: ['production'],
    timeWindow: 'Business hours 08:00–18:00 ET',
    rateLimitPerMinute: 8,
    maxRecords: 1,
    riskLevel: 'critical',
    requiresApproval: true,
    sensitiveDataRestricted: true,
    outputRestricted: true,
    inheritSelficPermissions: true,
    effect: 'require_approval',
    status: 'active',
    createdAt: '2026-03-20T16:00:00.000Z',
    updatedAt: '2026-09-08T09:40:00.000Z',
    createdBy: 'Marcus Chen',
  },
  {
    id: 'pol-deny-prod-browser',
    name: 'Deny Playwright on production',
    description: 'External MCP browser tools must never appear on production endpoints.',
    userIds: [],
    groupIds: [],
    clientIds: [],
    endpointIds: [SINGLETON_ENDPOINT_ID],
    publishedCapabilityIds: ['pub-browser'],
    allowDiscovery: false,
    allowInvocation: false,
    readOnly: true,
    environments: ['production'],
    timeWindow: 'Always',
    rateLimitPerMinute: 0,
    maxRecords: 0,
    riskLevel: 'high',
    requiresApproval: false,
    sensitiveDataRestricted: true,
    outputRestricted: true,
    inheritSelficPermissions: true,
    effect: 'deny',
    status: 'active',
    createdAt: '2026-08-01T10:00:00.000Z',
    updatedAt: '2026-08-01T10:00:00.000Z',
    createdBy: 'Priya Shah',
  },
];

function steps(
  decision: ActivityEvent['decision'],
  extra: Partial<Record<string, string>> = {}
): TraceStep[] {
  const failedAt =
    decision === 'failed'
      ? 'Authentication'
      : decision === 'denied'
        ? 'Authorization'
        : decision === 'approval_required'
          ? 'Policy Evaluation'
          : null;

  const labels = [
    'AI Request',
    'Authentication',
    'User Resolution',
    'Client Validation',
    'Capability Discovery/Invocation',
    'Policy Evaluation',
    'Authorization',
    'Execution',
    'Output Governance',
    'MCP Response',
  ];

  return labels.map((label, index) => {
    let status: TraceStep['status'] = 'passed';
    let reason = extra[label] || `${label} completed under Selfic control.`;
    if (failedAt && labels.indexOf(failedAt) < index) {
      status = label === 'MCP Response' ? 'passed' : 'skipped';
      reason = extra[label] || 'Skipped because a prior governance step blocked execution.';
    }
    if (label === failedAt) {
      status = decision === 'approval_required' ? 'blocked' : 'failed';
      if (decision === 'denied') reason = extra[label] || 'Policy denied this client/user/capability combination.';
      if (decision === 'approval_required') reason = extra[label] || 'Policy requires human approval before execution.';
      if (decision === 'failed') reason = extra[label] || 'Step failed.';
    }
    if (decision === 'approval_required' && label === 'Execution') {
      status = 'blocked';
      reason = 'Execution held pending claims supervisor approval.';
    }
    return {
      id: `step-${index}`,
      label,
      status,
      reason,
      at: new Date(Date.parse('2026-09-16T18:00:00.000Z') + index * 40).toISOString(),
    };
  });
}

export const SEED_ACTIVITY: ActivityEvent[] = [
  {
    id: 'act-1',
    timestamp: '2026-09-16T18:42:11.000Z',
    requestId: 'req-8f2a1c',
    tenant: TENANT_NAME,
    userId: 'u-priya',
    userName: 'Priya Shah',
    groupNames: ['Risk Analytics'],
    roles: ['Risk Admin'],
    clientId: 'cl-claude',
    clientName: 'Claude Enterprise — Risk Desk',
    endpointId: SINGLETON_ENDPOINT_ID,
    endpointName: 'Finance Production MCP',
    operation: 'invoke_tool',
    capabilityId: 'pub-exposure',
    capabilityName: 'get_customer_exposure',
    capabilityKind: 'information',
    representations: ['tool'],
    inputSummary: 'customer_id=ACME-44012 as_of_date=2026-09-15',
    policiesEvaluated: ['Risk desk — exposure read'],
    decision: 'allowed',
    approvalStatus: 'not_required',
    dataSources: ['Customer Exposure'],
    classificationsEncountered: ['confidential'],
    recordsAccessed: 1,
    fieldsMasked: ['internal_score'],
    workflowRef: null,
    outputClassification: 'confidential',
    durationMs: 412,
    result: 'Returned 1 exposure record. Masked internal_score.',
    error: null,
    trace: steps('allowed', {
      Authorization: 'Allow — user in Risk Analytics, client registered, capability published to production.',
      'Output Governance': 'Masked internal_score per classification policy.',
    }),
  },
  {
    id: 'act-2',
    timestamp: '2026-09-16T16:55:04.000Z',
    requestId: 'req-3bb91e',
    tenant: TENANT_NAME,
    userId: 'u-marcus',
    userName: 'Marcus Chen',
    groupNames: ['Claims Operations'],
    roles: ['Claims Lead'],
    clientId: 'cl-claims',
    clientName: 'Internal Claims Agent',
    endpointId: SINGLETON_ENDPOINT_ID,
    endpointName: 'Finance Production MCP',
    operation: 'invoke_tool',
    capabilityId: 'pub-adjust',
    capabilityName: 'submit_claims_adjustment',
    capabilityKind: 'action',
    representations: ['tool'],
    inputSummary: 'claim_id=CLM-8821 adjustment_usd=15000 reason="reserve correction"',
    policiesEvaluated: ['Claims adjustment — approval required'],
    decision: 'approval_required',
    approvalStatus: 'pending',
    dataSources: ['Claims History'],
    classificationsEncountered: ['restricted'],
    recordsAccessed: 1,
    fieldsMasked: ['ssn_last4'],
    workflowRef: 'WF_CLAIMS_ADJUST#8821',
    outputClassification: 'restricted',
    durationMs: 188,
    result: 'Held for human approval. Workflow not started.',
    error: null,
    trace: steps('approval_required', {
      'Policy Evaluation': 'Effect = Require approval because capability is destructive and risk is critical.',
    }),
  },
  {
    id: 'act-3',
    timestamp: '2026-09-16T15:21:44.000Z',
    requestId: 'req-aa17d0',
    tenant: TENANT_NAME,
    userId: 'u-elena',
    userName: 'Elena Rossi',
    groupNames: ['Legal & Compliance'],
    roles: ['Counsel'],
    clientId: 'cl-chatgpt',
    clientName: 'ChatGPT Enterprise — Legal',
    endpointId: SINGLETON_ENDPOINT_ID,
    endpointName: 'Finance Production MCP',
    operation: 'invoke_tool',
    capabilityId: 'pub-search-policy',
    capabilityName: 'search_policy_terms',
    capabilityKind: 'search',
    representations: ['tool'],
    inputSummary: 'query="flood exclusion New York" limit=10',
    policiesEvaluated: ['Legal — policy term search'],
    decision: 'allowed',
    approvalStatus: 'not_required',
    dataSources: ['Policy Terms'],
    classificationsEncountered: ['internal'],
    recordsAccessed: 8,
    fieldsMasked: [],
    workflowRef: null,
    outputClassification: 'internal',
    durationMs: 267,
    result: 'Returned 8 clause snippets.',
    error: null,
    trace: steps('allowed'),
  },
  {
    id: 'act-4',
    timestamp: '2026-09-16T14:02:18.000Z',
    requestId: 'req-d09c22',
    tenant: TENANT_NAME,
    userId: 'u-james',
    userName: 'James Okonkwo',
    groupNames: ['KYC Operations'],
    roles: ['KYC Analyst'],
    clientId: 'cl-claude',
    clientName: 'Claude Enterprise — Risk Desk',
    endpointId: SINGLETON_ENDPOINT_ID,
    endpointName: 'Finance Production MCP',
    operation: 'invoke_tool',
    capabilityId: 'pub-adjust',
    capabilityName: 'submit_claims_adjustment',
    capabilityKind: 'action',
    representations: ['tool'],
    inputSummary: 'claim_id=CLM-1102 adjustment_usd=500 reason="test"',
    policiesEvaluated: ['Claims adjustment — approval required'],
    decision: 'denied',
    approvalStatus: 'not_applicable',
    dataSources: [],
    classificationsEncountered: [],
    recordsAccessed: 0,
    fieldsMasked: [],
    workflowRef: null,
    outputClassification: 'internal',
    durationMs: 94,
    result: 'Denied. User is not in Claims Operations and client is not the claims agent.',
    error: 'authorization_denied',
    trace: steps('denied', {
      Authorization:
        'Deny — Selfic permissions and MCP policy both exclude KYC analysts from claims adjustment. AI client cannot override.',
    }),
  },
  {
    id: 'act-5',
    timestamp: '2026-09-16T12:40:00.000Z',
    requestId: 'req-kyc-77',
    tenant: TENANT_NAME,
    userId: 'u-james',
    userName: 'James Okonkwo',
    groupNames: ['KYC Operations'],
    roles: ['KYC Analyst'],
    clientId: 'cl-claims',
    clientName: 'Internal Claims Agent',
    endpointId: SINGLETON_ENDPOINT_ID,
    endpointName: 'Finance Production MCP',
    operation: 'start_task',
    capabilityId: 'pub-kyc',
    capabilityName: 'start_kyc_refresh',
    capabilityKind: 'process',
    representations: ['tool', 'task'],
    inputSummary: 'customer_id=ACME-44012',
    policiesEvaluated: [],
    decision: 'allowed',
    approvalStatus: 'not_required',
    dataSources: ['Customer Exposure'],
    classificationsEncountered: ['restricted'],
    recordsAccessed: 1,
    fieldsMasked: ['ssn_last4'],
    workflowRef: 'WF_KYC_REFRESH#t-44012',
    outputClassification: 'restricted',
    durationMs: 640,
    result: 'Task t-44012 queued (22% vendor screening).',
    error: null,
    trace: steps('allowed', {
      Execution: 'Long-running workflow started. Task id t-44012.',
    }),
  },
  {
    id: 'act-6',
    timestamp: '2026-09-16T17:05:00.000Z',
    requestId: 'req-nav-9',
    tenant: TENANT_NAME,
    userId: 'u-marcus',
    userName: 'Marcus Chen',
    groupNames: ['Claims Operations'],
    roles: ['Claims Lead'],
    clientId: 'cl-claude',
    clientName: 'Claude Enterprise — Risk Desk',
    endpointId: SINGLETON_ENDPOINT_ID,
    endpointName: 'Sandbox MCP',
    operation: 'invoke_tool',
    capabilityId: 'pub-browser',
    capabilityName: 'browser_navigate',
    capabilityKind: 'external_mcp',
    representations: ['tool'],
    inputSummary: 'url=https://sandbox.selfic.local/claims',
    policiesEvaluated: ['Deny Playwright on production'],
    decision: 'allowed',
    approvalStatus: 'not_required',
    dataSources: ['Playwright Browser'],
    classificationsEncountered: ['internal'],
    recordsAccessed: 0,
    fieldsMasked: [],
    workflowRef: null,
    outputClassification: 'internal',
    durationMs: 880,
    result: 'Navigated sandbox browser. Production deny policy not applicable.',
    error: null,
    trace: steps('allowed', {
      Authorization: 'Allow on development endpoint only. External MCP tools remain governed by Selfic.',
    }),
  },
];

export const SEED_API_KEYS: GatewayApiKey[] = [];

function quotaDay(offsetFromStart: number): string {
  const day = new Date();
  day.setHours(12, 0, 0, 0);
  day.setDate(day.getDate() - (13 - offsetFromStart));
  return day.toISOString().slice(0, 10);
}

function dailyUsage(used: number, salt: number): QuotaDailyPoint[] {
  const weights = Array.from({ length: 14 }, (_, index) => {
    const wave = 0.62 + 0.38 * Math.sin((index + salt) * 0.68);
    return wave * (0.72 + ((salt * (index + 4)) % 6) * 0.06);
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  return weights.map((weight, index) => ({
    date: quotaDay(index),
    tokens: Math.max(0, Math.round((weight / total) * used)),
  }));
}

function modelSpend(used: number, mix: [string, number][]): QuotaModelSpend[] {
  const total = mix.reduce((sum, [, share]) => sum + share, 0);
  return mix.map(([model, share]) => ({
    model,
    tokens: Math.round((share / total) * used),
  }));
}

function quotaAssignment(
  id: string,
  clientId: string,
  person: { name: string; email: string; role: string },
  tokenLimit: number,
  tokensUsed: number,
  salt: number,
  mix: [string, number][],
  updatedAt: string
): QuotaUser {
  return {
    id,
    clientId,
    name: person.name,
    email: person.email,
    role: person.role,
    tokenLimit,
    tokensUsed,
    dailyUsage: dailyUsage(tokensUsed, salt),
    byModel: modelSpend(tokensUsed, mix),
    updatedAt,
  };
}

const PERSON = {
  priya: { name: 'Priya Shah', email: 'priya.shah@acme.financial', role: 'Chief Risk Officer' },
  marcus: { name: 'Marcus Chen', email: 'marcus.chen@acme.financial', role: 'Claims Operations Lead' },
  elena: { name: 'Elena Rossi', email: 'elena.rossi@acme.financial', role: 'Legal Counsel' },
  james: { name: 'James Okonkwo', email: 'james.okonkwo@acme.financial', role: 'KYC Analyst' },
  aisha: { name: 'Aisha Rahman', email: 'aisha.rahman@acme.financial', role: 'Lead Data Scientist' },
  daniel: { name: 'Daniel Park', email: 'daniel.park@acme.financial', role: 'Product Manager' },
  sofia: { name: 'Sofia Alvarez', email: 'sofia.alvarez@acme.financial', role: 'Customer Support Lead' },
  noah: { name: 'Noah Williams', email: 'noah.williams@acme.financial', role: 'Platform Engineer' },
};

export const SEED_QUOTA_USERS: QuotaUser[] = [
  quotaAssignment('quota-priya-claude', 'cl-claude', PERSON.priya, 2_000_000, 1_240_000, 3, [
    ['Claude 3.5 Sonnet', 0.46],
    ['GPT-4o', 0.32],
    ['Internal Agent', 0.22],
  ], '2026-09-18T14:20:00.000Z'),
  quotaAssignment('quota-priya-claims', 'cl-claims', PERSON.priya, 400_000, 96_200, 8, [
    ['GPT-4o', 0.7],
    ['Internal Agent', 0.3],
  ], '2026-09-16T09:10:00.000Z'),
  quotaAssignment('quota-marcus-claims', 'cl-claims', PERSON.marcus, 1_500_000, 1_310_000, 5, [
    ['GPT-4o', 0.54],
    ['Claude 3.5 Sonnet', 0.28],
    ['Gemini 2.0 Flash', 0.18],
  ], '2026-09-19T09:05:00.000Z'),
  quotaAssignment('quota-marcus-copilot', 'cl-copilot', PERSON.marcus, 350_000, 142_000, 12, [
    ['GPT-4o', 0.6],
    ['Gemini 2.0 Flash', 0.4],
  ], '2026-09-14T11:22:00.000Z'),
  quotaAssignment('quota-elena-chatgpt', 'cl-chatgpt', PERSON.elena, 800_000, 812_400, 2, [
    ['Claude 3.5 Sonnet', 0.71],
    ['GPT-4o', 0.29],
  ], '2026-09-20T11:40:00.000Z'),
  quotaAssignment('quota-james-claims', 'cl-claims', PERSON.james, 1_000_000, 418_600, 7, [
    ['GPT-4o', 0.41],
    ['Gemini 2.0 Flash', 0.35],
    ['Internal Agent', 0.24],
  ], '2026-09-17T16:12:00.000Z'),
  quotaAssignment('quota-james-copilot', 'cl-copilot', PERSON.james, 250_000, 88_400, 14, [
    ['Gemini 2.0 Flash', 0.58],
    ['GPT-4o', 0.42],
  ], '2026-09-15T08:48:00.000Z'),
  quotaAssignment('quota-aisha-chatgpt', 'cl-chatgpt', PERSON.aisha, 3_000_000, 1_860_000, 11, [
    ['GPT-4o', 0.38],
    ['Claude 3.5 Sonnet', 0.34],
    ['Gemini 2.0 Flash', 0.28],
  ], '2026-09-21T08:30:00.000Z'),
  quotaAssignment('quota-aisha-claude', 'cl-claude', PERSON.aisha, 600_000, 274_000, 15, [
    ['Claude 3.5 Sonnet', 0.55],
    ['GPT-4o', 0.45],
  ], '2026-09-18T19:05:00.000Z'),
  quotaAssignment('quota-daniel-chatgpt', 'cl-chatgpt', PERSON.daniel, 500_000, 214_800, 4, [
    ['GPT-4o', 0.62],
    ['Internal Agent', 0.38],
  ], '2026-09-16T13:55:00.000Z'),
  quotaAssignment('quota-sofia-copilot', 'cl-copilot', PERSON.sofia, 750_000, 691_200, 9, [
    ['Gemini 2.0 Flash', 0.48],
    ['GPT-4o', 0.33],
    ['Internal Agent', 0.19],
  ], '2026-09-20T18:22:00.000Z'),
  quotaAssignment('quota-noah-claims', 'cl-claims', PERSON.noah, 2_500_000, 976_400, 6, [
    ['Internal Agent', 0.44],
    ['Claude 3.5 Sonnet', 0.31],
    ['GPT-4o', 0.25],
  ], '2026-09-19T21:10:00.000Z'),
  quotaAssignment('quota-noah-claude', 'cl-claude', PERSON.noah, 900_000, 188_000, 10, [
    ['Internal Agent', 0.66],
    ['GPT-4o', 0.34],
  ], '2026-09-17T07:40:00.000Z'),
];
